import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { initialState } from "./initialState";
import { getUserThunk, updateUser, verifyUserNewEmail, getAmazonCredentialsThunk } from "./user.thunk";
import { USER_ERRORS } from "../../constants/user";
import { LocalStorageKeys } from "../../constants";
import { TSetTokensPayload, TUserAmazonAccount } from "./user.types";

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    resetUserState: (state) => {
      localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
      localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
      state.token = null;
      state.user = null;
      state.error = null;
      state.loading = false;
    },
    setTokens: (state, action: PayloadAction<TSetTokensPayload>) => {
      const { accessToken, refreshToken } = action.payload;
      if (accessToken) {
        state.token = accessToken;
        localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, accessToken);
      } else if (accessToken === null) {
        state.token = null;
        localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
      }
      if (refreshToken) {
        localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, refreshToken);
      } else if (refreshToken === null) {
        localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
      }
    },
    increaseLookupUsage: (state) => {
      if (!state.user) return;
      state.user.usage.analyzerLookupsUsed++;
    },
    setAmazonAccount: (state, action: PayloadAction<TUserAmazonAccount>) => {
      state.amazonAccount = {
        ...state.amazonAccount,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload;
        state.loaded = true;
      })
      .addCase(getUserThunk.rejected, (state) => {
        state.loading = false;
        state.error = USER_ERRORS.FAILED_TO_FETCH_USER;
        state.loaded = true;
      });

    builder
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload;
      })
      .addCase(updateUser.rejected, (state) => {
        state.loading = false;
        state.error = USER_ERRORS.FAILED_TO_UPDATE_USER;
      });

    builder.addCase(getAmazonCredentialsThunk.fulfilled, (state, action) => {
      state.amazonAccount = {
        ...state.amazonAccount,
        ...action.payload,
      };
    });

    builder.addCase(verifyUserNewEmail.fulfilled, (state, action) => {
      if (!state.user) return;
      state.user.email = action.payload;
    });
  },
});

export const {
  resetUserState,
  setTokens,
  setAmazonAccount,
  increaseLookupUsage,
} = userSlice.actions;
export default userSlice.reducer;
